import * as React from 'react';

/**
 * Rastreia se um formulário foi alterado desde o estado inicial (CON-4980).
 * Guarda um baseline (snapshot do 1º valor "carregado", ignorando null/undefined)
 * e compara o valor atual por serialização. Expõe `isDirty` e `rebase()` — chamar
 * `rebase()` após salvar para zerar o "sujo" sem recarregar a tela.
 */
export function useDirty<T>(value: T): { isDirty: boolean; rebase: (next?: T) => void } {
  const baseline = React.useRef<string | undefined>(undefined);
  const [isDirty, setDirty] = React.useState(false);
  const key = value == null ? null : JSON.stringify(value);

  React.useEffect(() => {
    if (key === null) return; // ainda não carregou — não define baseline nem marca dirty
    if (baseline.current === undefined) {
      baseline.current = key; // 1º valor carregado vira o baseline
    }
    setDirty(key !== baseline.current);
  }, [key]);

  // rebase() usa o valor atual; rebase(x) fixa `x` como baseline — útil quando o form
  // parte de um valor não-nulo (ex.: FORM_VAZIO) e o baseline correto é o dado carregado.
  const rebase = React.useCallback(
    (next?: T) => {
      const v = next === undefined ? value : next;
      baseline.current = v == null ? undefined : JSON.stringify(v);
      setDirty(false);
    },
    [value],
  );

  return { isDirty, rebase };
}
